import type { ConnectionDetailsHeaders } from "@/lib/livekit-agent-connection-request";
import type { LiveKitConnectionDetailsTokenPayload } from "@/lib/livekit-agent-token-cache";
import {
  CONNECTION_DETAILS_FETCH_TIMEOUT_MS,
  fetchConnectionDetailsWithRetry,
} from "@/lib/livekit-connection-details-fetch";

export type Auth401RetryPhase = 0 | 1 | 2;

type RecoveryLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
};

type PostHogLike = {
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
};

type RefreshSessionResult = {
  data?: {
    session?: {
      access_token?: string | null;
    } | null;
  } | null;
  error?: {
    message?: string;
  } | null;
};

const GUEST_FALLBACK_AUTH_SCOPED_FIELDS = [
  "memoriesSummary",
  "preloadedMemoriesCount",
  "preloadedQdrantContext",
  "classId",
  "teacherPreferences",
  "conversationId",
] as const;

function buildGuestFallbackBody(body: string): string {
  const parsed: unknown = JSON.parse(body);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Connection details request body must be a JSON object");
  }

  const guestBody = { ...(parsed as Record<string, unknown>) };
  for (const field of GUEST_FALLBACK_AUTH_SCOPED_FIELDS) {
    delete guestBody[field];
  }
  return JSON.stringify(guestBody);
}

export type RecoverUnauthorizedConnectionDetailsInput = {
  isCurrentAttempt?: () => boolean;
  url: URL;
  method: "POST";
  headers: ConnectionDetailsHeaders;
  body: string;
  getRetryPhase: () => Auth401RetryPhase;
  setRetryPhase: (phase: Auth401RetryPhase) => void;
  maxRetryPhases: number;
  hasCachedToken: () => boolean;
  clearTokenCache: () => void;
  isRetryingAsGuest: boolean;
  setIsRetryingAsGuest: (isRetrying: boolean) => void;
  setConnectionError: (message: string) => void;
  setConnectionState: (state: "failed") => void;
  setIsConnected: (isConnected: boolean) => void;
  setAgentState: (state: "disconnected") => void;
  setIsRecording: (isRecording: boolean) => void;
  resetConnectionElapsedTimer: () => void;
  refreshSession: () => Promise<RefreshSessionResult>;
  parseConnectionDetailsResponse: (
    response: Response
  ) => Promise<LiveKitConnectionDetailsTokenPayload>;
  cacheAndStartConnectionDetails: (
    connectionDetails: LiveKitConnectionDetailsTokenPayload
  ) => Promise<void>;
  fetchFn?: typeof fetch;
  fetchTimeoutMs?: number;
  posthogClient?: PostHogLike | null;
  hasPosthogKey: boolean;
  selectedLanguageCode: string;
  userType: "authenticated" | "guest";
  logger: RecoveryLogger;
};

export async function recoverUnauthorizedConnectionDetails({
  isCurrentAttempt = () => true,
  url,
  method,
  headers,
  body,
  getRetryPhase,
  setRetryPhase,
  maxRetryPhases,
  hasCachedToken,
  clearTokenCache,
  isRetryingAsGuest,
  setIsRetryingAsGuest,
  setConnectionError,
  setConnectionState,
  setIsConnected,
  setAgentState,
  setIsRecording,
  resetConnectionElapsedTimer,
  refreshSession,
  parseConnectionDetailsResponse,
  cacheAndStartConnectionDetails,
  fetchFn = fetch,
  fetchTimeoutMs = CONNECTION_DETAILS_FETCH_TIMEOUT_MS,
  posthogClient,
  hasPosthogKey,
  selectedLanguageCode,
  userType,
  logger,
}: RecoverUnauthorizedConnectionDetailsInput): Promise<boolean> {
  if (!isCurrentAttempt()) return false;
  const fetchConnectionDetails = async (
    requestHeaders: ConnectionDetailsHeaders,
    requestBody = body
  ) => {
    const result = await fetchConnectionDetailsWithRetry({
      input: url.toString(),
      init: {
        method,
        headers: requestHeaders,
        body: requestBody,
        cache: "no-store",
      },
      fetchFn,
      timeoutMs: fetchTimeoutMs,
      // Authentication recovery already has same-token, refreshed-token, and
      // guest phases. Bound every phase without multiplying those semantic retries.
      maxAttempts: 1,
      retryDelayMs: 0,
    });
    return result.response;
  };

  const currentPhase = getRetryPhase();
  logger.warn("Authentication failed (401)", {
    phase: currentPhase,
    maxPhases: maxRetryPhases,
    hasCachedToken: hasCachedToken(),
  });

  if (currentPhase === 0) {
    setRetryPhase(1);
    logger.info("401 Phase 1: Retrying with same credentials after brief delay (transient check)");
    setConnectionError("Connection issue detected. Retrying...");

    await new Promise((resolve) => setTimeout(resolve, 500));
    if (!isCurrentAttempt()) return false;

    try {
      const retryResponse = await fetchConnectionDetails(headers);
      if (!isCurrentAttempt()) return false;
      if (retryResponse.ok) {
        const connectionDetails = await parseConnectionDetailsResponse(retryResponse);
        if (!isCurrentAttempt()) return false;
        logger.info("401 Phase 1 SUCCESS: Transient issue resolved");
        setRetryPhase(0);
        await cacheAndStartConnectionDetails(connectionDetails);
        return isCurrentAttempt();
      }
      logger.warn("401 Phase 1 FAILED: Same-token retry unsuccessful, proceeding to Phase 2");
    } catch (retryError) {
      if (!isCurrentAttempt()) return false;
      logger.warn("401 Phase 1 exception:", retryError);
    }
  }

  if (getRetryPhase() <= 1) {
    setRetryPhase(2);
    logger.info("401 Phase 2: Refreshing session and fetching fresh token");
    setConnectionError("Refreshing authentication...");
    clearTokenCache();

    try {
      const { data, error: refreshError } = await refreshSession();
      if (!isCurrentAttempt()) return false;

      if (refreshError) {
        logger.error("Session refresh failed:", refreshError);
        if (
          refreshError.message?.includes("Invalid Refresh Token") ||
          refreshError.message?.includes("Refresh Token Not Found")
        ) {
          logger.info("Invalid refresh token - will attempt guest fallback");
        }
      } else if (data?.session?.access_token) {
        logger.info("Session refresh successful, retrying with fresh token");

        const freshHeaders = {
          ...headers,
          Authorization: `Bearer ${data.session.access_token}`,
        };
        const freshResponse = await fetchConnectionDetails(freshHeaders);
        if (!isCurrentAttempt()) return false;

        if (freshResponse.ok) {
          const connectionDetails = await parseConnectionDetailsResponse(freshResponse);
          if (!isCurrentAttempt()) return false;
          logger.info("401 Phase 2 SUCCESS: Fresh token worked");
          setRetryPhase(0);
          await cacheAndStartConnectionDetails(connectionDetails);
          return isCurrentAttempt();
        }

        logger.warn("401 Phase 2 FAILED: Fresh token still unauthorized");
      }
    } catch (refreshError) {
      if (!isCurrentAttempt()) return false;
      logger.error("Exception during session refresh:", refreshError);
    }
  }

  if (!isRetryingAsGuest) {
    logger.info("401 All phases exhausted: Falling back to guest mode");
    setIsRetryingAsGuest(true);
    setRetryPhase(0);

    const guestHeaders = { ...headers };
    delete guestHeaders.Authorization;

    try {
      const guestResponse = await fetchConnectionDetails(
        guestHeaders,
        buildGuestFallbackBody(body)
      );
      if (!isCurrentAttempt()) return false;

      if (guestResponse.ok) {
        const connectionDetails = await parseConnectionDetailsResponse(guestResponse);
        if (!isCurrentAttempt()) return false;
        logger.info("Guest fallback SUCCESS");
        await cacheAndStartConnectionDetails(connectionDetails);
        return isCurrentAttempt();
      }

      const guestErrorText = await guestResponse.text();
      if (!isCurrentAttempt()) return false;
      logger.error("Guest fallback FAILED", {
        status: guestResponse.status,
        error: guestErrorText,
      });
    } catch (guestError) {
      if (!isCurrentAttempt()) return false;
      logger.error("Exception during guest fallback", { error: guestError });
    }
  }

  setRetryPhase(0);
  setConnectionState("failed");
  setConnectionError(
    "Unable to connect. Please try again, or sign out and sign back in if the problem persists."
  );
  setIsConnected(false);
  setAgentState("disconnected");
  setIsRecording(false);
  resetConnectionElapsedTimer();

  if (posthogClient && hasPosthogKey) {
    posthogClient.capture("livekit_401_all_retries_exhausted", {
      language_code: selectedLanguageCode,
      user_type: userType,
      was_guest_fallback_attempted: isRetryingAsGuest,
    });
  }

  return false;
}
